import { sql, relations } from 'drizzle-orm'
import { sqliteTable, text, integer, index, uniqueIndex, check } from 'drizzle-orm/sqlite-core'

/* ===================================================================== */
/* Le tronc commun de synchronisation                                    */
/* ===================================================================== */
/*
 * Ces six colonnes sont portées par TOUTE table de contenu. Chacune répond
 * à un problème précis du hors-ligne ; aucune n'est là « au cas où ».
 *
 * Elles ne sont pas sur `user`, `session` ni sur les tables OAuth : ces
 * objets-là n'existent que côté serveur et ne descendent jamais sur un
 * téléphone.
 */
const colonnesSync = {
  /**
   * UUIDv7 GÉNÉRÉ PAR LE CLIENT (voir src/lib/ids.ts). Un identifiant
   * attribué par le serveur imposerait un aller-retour réseau pour créer
   * quoi que ce soit — donc pas de création dans le supermarché.
   */
  id: text('id').primaryKey(),

  householdId: text('household_id')
    .notNull()
    .references(() => households.id, { onDelete: 'cascade' }),

  /**
   * LE curseur de synchronisation. Incrémenté depuis `households.seq` à
   * CHAQUE écriture, dans la même transaction. C'est lui qui répond à
   * « qu'est-ce qui a changé depuis ma dernière visite », et il le fait sans
   * jamais consulter une horloge — ni celle du client, ni celle du serveur.
   * C'est ce qui rend toute la synchro insensible à la dérive d'horloge.
   */
  rev: integer('rev').notNull(),

  /**
   * Pierre tombale. Une suppression DOIT être transportable par le même
   * pull que les modifications : une ligne réellement effacée disparaîtrait
   * du delta, et un téléphone resté en veille la garderait pour l'éternité.
   * Purgée après 90 jours, au-delà de quoi le client reçoit un `reset`.
   */
  deletedAt: integer('deleted_at', { mode: 'timestamp' }),

  /**
   * Horloge SERVEUR, à l'affichage seulement (« modifié il y a 3 min »).
   * N'intervient JAMAIS dans la résolution de conflit : c'est précisément
   * le piège du last-write-wins horodaté qu'on refuse ici.
   */
  updatedAt: integer('updated_at', { mode: 'timestamp' })
    .notNull()
    .default(sql`(unixepoch())`),

  /** Qui. Sert à l'UI (« coché par Tristan ») et au débogage des conflits. */
  updatedBy: text('updated_by'),
}

/* ===================================================================== */
/* Foyer, appareils, journal                                             */
/* ===================================================================== */

/**
 * Foyer. Porte `seq`, le compteur monotone qui sert de curseur à tous ses
 * appareils. Un compteur PAR FOYER et non global : il ne fuit pas l'activité
 * des autres foyers et il reste petit.
 */
export const households = sqliteTable('households', {
  id: text('id').primaryKey(),
  name: text('name').notNull(),
  /** Incrémenté dans la transaction de chaque écriture. Ordre total gratuit. */
  seq: integer('seq').notNull().default(0),
  /** Version de graine déjà appliquée — voir db/seed/apply.ts. */
  seedVersion: integer('seed_version').notNull().default(0),
  /**
   * En dessous de ce `rev`, les pierres tombales ont été purgées : on ne
   * peut plus produire un delta correct. Un client qui demande `since` plus
   * ancien reçoit `{ reset: true }` et repart de zéro, plutôt que de
   * ressusciter silencieusement des articles supprimés.
   */
  purgeWatermark: integer('purge_watermark').notNull().default(0),
  createdAt: integer('created_at', { mode: 'timestamp' })
    .notNull()
    .default(sql`(unixepoch())`),
})

/**
 * Appareil. Sans lui, impossible d'ordonner entre elles les opérations d'un
 * même téléphone quand son horloge saute : c'est `lastDeviceSeq` qui porte
 * cet ordre, pas une date.
 * Sert aussi au diagnostic — « le téléphone de Marie n'a pas poussé depuis
 * trois jours » est l'information qui sauve une soirée.
 */
export const devices = sqliteTable(
  'devices',
  {
    id: text('id').primaryKey(),
    userId: text('user_id').notNull(),
    householdId: text('household_id')
      .notNull()
      .references(() => households.id, { onDelete: 'cascade' }),
    lastSeenRev: integer('last_seen_rev').notNull().default(0),
    lastDeviceSeq: integer('last_device_seq').notNull().default(0),
    lastPushAt: integer('last_push_at', { mode: 'timestamp' }),
    userAgent: text('user_agent'),
  },
  (t) => [index('devices_household').on(t.householdId)],
)

/**
 * Journal des intentions reçues. Trois rôles, tous indispensables :
 *
 *  - IDEMPOTENCE : `opId` en clé primaire. Une réponse perdue fait renvoyer
 *    l'opération ; le serveur la reconnaît et répond « déjà appliquée » au
 *    lieu de cocher deux fois.
 *  - ORDRE : (deviceId, deviceSeq) ordonne les opérations d'un appareil
 *    entre elles ; `rev` les ordonne entre appareils.
 *  - AUDIT : « qui a mis ça sur la liste, et quand » est la question qu'on
 *    se pose toutes les semaines. C'est aussi le seul moyen de rejouer un
 *    conflit après coup pour comprendre un bug.
 */
export const syncOps = sqliteTable(
  'sync_ops',
  {
    opId: text('op_id').primaryKey(),
    householdId: text('household_id')
      .notNull()
      .references(() => households.id, { onDelete: 'cascade' }),
    deviceId: text('device_id').notNull(),
    userId: text('user_id').notNull(),
    deviceSeq: integer('device_seq').notNull(),
    kind: text('kind').notNull(),
    payload: text('payload', { mode: 'json' }).notNull(),
    /**
     * Le `rev` que l'appareil avait déjà vu au moment du geste. C'est la
     * SEULE information de causalité du protocole : elle distingue « il
     * savait et il a quand même choisi ça » de « il ne pouvait pas savoir ».
     * C'est elle qui arbitre le conflit épuisé/racheté, pas une date.
     */
    baseRev: integer('base_rev').notNull(),
    /** Horloge du CLIENT. Conservée pour l'enquête, jamais pour arbitrer. */
    clientAt: integer('client_at', { mode: 'timestamp' }),
    receivedAt: integer('received_at', { mode: 'timestamp' })
      .notNull()
      .default(sql`(unixepoch())`),
    rev: integer('rev').notNull(),
    /** 'applied' | 'rejected' | 'adjusted' */
    status: text('status').notNull(),
    rejectReason: text('reject_reason'),
  },
  (t) => [
    index('sync_ops_household').on(t.householdId, t.receivedAt),
    uniqueIndex('sync_ops_device_seq').on(t.deviceId, t.deviceSeq),
  ],
)

/* ===================================================================== */
/* Tables de contenu                                                     */
/* ===================================================================== */

/**
 * Lieu de rangement. `isFreezer` n'est pas décoratif : c'est lui qui décide
 * si le formulaire propose spontanément une date de péremption. Ailleurs,
 * la DLC est du bruit qu'on ne remplira jamais.
 */
export const locations = sqliteTable(
  'locations',
  {
    ...colonnesSync,
    name: text('name').notNull(),
    emoji: text('emoji'),
    sortOrder: integer('sort_order').notNull().default(0),
    isFreezer: integer('is_freezer', { mode: 'boolean' }).notNull().default(false),
    seedSlug: text('seed_slug'),
  },
  (t) => [index('locations_pull').on(t.householdId, t.rev)],
)

/**
 * Rayon de supermarché. PAR FOYER et réordonnable, parce que l'ordre des
 * rayons qui compte est celui du magasin où l'on va réellement, pas celui
 * d'une nomenclature. C'est `sortOrder` qui fait marcher le mode magasin :
 * on traverse le magasin une fois, dans l'ordre.
 */
export const rayons = sqliteTable(
  'rayons',
  {
    ...colonnesSync,
    name: text('name').notNull(),
    emoji: text('emoji'),
    sortOrder: integer('sort_order').notNull().default(0),
    seedSlug: text('seed_slug'),
  },
  (t) => [index('rayons_pull').on(t.householdId, t.rev)],
)

/**
 * CATALOGUE : ce qui existe dans le monde.
 *
 * À ne pas confondre avec l'inventaire, qui est ce qu'on POSSÈDE. La
 * séparation est nécessaire pour deux raisons : la graine ne doit pas
 * prétendre qu'on possède 750 produits, et un même produit peut être à la
 * fois au frigo et au congélateur.
 */
export const catalogItems = sqliteTable(
  'catalog_items',
  {
    ...colonnesSync,
    name: text('name').notNull(),
    /**
     * Colonne de recherche normalisée (nom + alias, sans accent, minuscules).
     * SQLite ne sait pas replier les diacritiques — son LIKE n'est
     * insensible à la casse qu'en ASCII — donc sans cette colonne « creme »
     * ne trouverait jamais « crème ». Calculée par src/lib/search.ts, LA
     * MÊME fonction que le client utilise en mémoire : c'est ce partage qui
     * garantit que les deux moteurs trouvent les mêmes résultats.
     */
    search: text('search').notNull(),
    aliases: text('aliases', { mode: 'json' }).$type<string[]>().notNull().default(sql`'[]'`),
    emoji: text('emoji'),
    defaultLocationId: text('default_location_id'),
    rayonId: text('rayon_id'),
    /** « L », « kg », « rouleau » — indicatif à l'affichage, jamais compté. */
    unitHint: text('unit_hint'),
    /** Durée de garde typique au congélateur, pré-remplit la DLC. */
    keepsDays: integer('keeps_days'),
    /*
     * PLUSIEURS codes-barres par produit, et c'est le cœur du modèle.
     * Ramener « Petits Pois Doux Très Fins CARREFOUR » sur « Petits pois »
     * est l'usage principal de la fusion ; chaque marque absorbée apporte
     * son EAN. Une colonne unique perdait forcément le second, et rescanner
     * le paquet recréait le doublon qu'on venait d'écraser.
     *
     * L'unicité est vérifiée dans `catalogueCreer`, pas par un index : elle
     * porte sur le CONTENU d'un tableau JSON, et l'opération dispose déjà
     * de tout l'état en mémoire pour la contrôler — c'est une règle
     * métier, pas une contrainte de stockage.
     */
    barcodes: text('barcodes', { mode: 'json' }).$type<string[]>().notNull().default(sql`'[]'`),
    /** Catégorie Open Food Facts brute, gardée telle quelle pour audit. */
    offCategory: text('off_category'),
    /** 'seed' | 'user' | 'barcode' — voir la règle d'évolution de la graine. */
    origin: text('origin').notNull().default('user'),
    seedSlug: text('seed_slug'),
    /**
     * 0 = produit de base, 1 = courant, 2 = rare. Classe l'autocomplétion :
     * « lait » doit proposer *Lait demi-écrémé* avant *Lait de coco*.
     */
    tier: integer('tier').notNull().default(1),
  },
  (t) => [
    index('catalog_pull').on(t.householdId, t.rev),
    index('catalog_search').on(t.householdId, t.search),
  ],
)

/**
 * INVENTAIRE : ce que le foyer possède, et où.
 *
 * Une ligne = un produit dans un lieu. Pas de quantité : c'est un choix
 * produit assumé. Un état en trois positions se met à jour d'un pouce, un
 * compteur non — et un inventaire qu'on ne tient pas devient faux, donc
 * inutile.
 */
export const inventoryItems = sqliteTable(
  'inventory_items',
  {
    ...colonnesSync,
    catalogItemId: text('catalog_item_id').notNull(),
    locationId: text('location_id').notNull(),
    /** 'en_stock' | 'bientot_fini' | 'epuise' */
    state: text('state').notNull().default('en_stock'),
    stateChangedAt: integer('state_changed_at', { mode: 'timestamp' }),
    /**
     * `rev` du dernier changement d'état. C'est la granularité fine dont la
     * règle de conflit prioritaire a besoin : elle permet de savoir si un
     * « épuisé » a été décidé AVANT ou APRÈS le passage en caisse, sans
     * avoir à horodater chaque champ de chaque table.
     */
    stateRev: integer('state_rev').notNull().default(0),
    /**
     * QUI a posé l'état courant : 'manuel' ou 'trip'.
     *
     * C'est ce qui donne son sens à la règle de conflit. Un état posé par une
     * validation de courses encode un fait physique — le produit est dans le
     * sac. Un état posé à la main depuis la cuisine encode une observation
     * sur un frigo qui n'a pas encore été rempli. Sans cette colonne, on ne
     * pourrait pas distinguer les deux, et il faudrait arbitrer à l'horloge.
     */
    stateSource: text('state_source').notNull().default('manuel'),
    note: text('note'),
  },
  (t) => [
    index('inventory_pull').on(t.householdId, t.rev),
    uniqueIndex('inventory_unique').on(t.householdId, t.catalogItemId, t.locationId),
    check('inventory_state', sql`${t.state} in ('en_stock','bientot_fini','epuise')`),
  ],
)

/**
 * DLC. Table fille et non colonne, parce qu'un congélateur contient trois
 * barquettes du même plat à trois dates différentes. C'est aussi ce qui
 * rend le congélateur utilisable sans réintroduire de quantités : on compte
 * les dates, pas les unités.
 */
export const expiries = sqliteTable(
  'expiries',
  {
    ...colonnesSync,
    inventoryItemId: text('inventory_item_id').notNull(),
    /** YYYY-MM-DD en texte : une DLC est un jour, pas un instant. */
    date: text('date').notNull(),
    label: text('label'),
  },
  (t) => [index('expiries_pull').on(t.householdId, t.rev), index('expiries_date').on(t.householdId, t.date)],
)

/**
 * LISTE DE COURSES. Une seule liste ouverte et permanente, et non « une
 * liste par sortie » : c'est comme ça qu'on fait ses courses. La sortie
 * (`trips`) ne fait qu'archiver ce qui a été pris.
 */
export const shoppingItems = sqliteTable(
  'shopping_items',
  {
    ...colonnesSync,
    catalogItemId: text('catalog_item_id'),
    freeText: text('free_text'),
    /**
     * Rayon DÉNORMALISÉ à l'ajout. Le mode magasin doit trier hors-ligne, y
     * compris un texte libre qui n'a aucune ligne de catalogue à joindre.
     * Une jointure vers une ligne éventuellement absente casserait le tri
     * au pire moment : au milieu du magasin.
     */
    rayonId: text('rayon_id'),
    rayonSortOrder: integer('rayon_sort_order').notNull().default(999),
    /** 'a_prendre' | 'pris' */
    state: text('state').notNull().default('a_prendre'),
    checkedAt: integer('checked_at', { mode: 'timestamp' }),
    checkedBy: text('checked_by'),
    /**
     * 'manuel' | 'stock' | 'menu' — d'où vient cette ligne. Permet
     * d'expliquer « pourquoi les échalotes sont là » et d'éviter de les
     * ré-ajouter en boucle.
     */
    addedReason: text('added_reason').notNull().default('manuel'),
    sourceRecipeId: text('source_recipe_id'),
    note: text('note'),
    /**
     * « 400 g », « 50 cl », « 3 ». Du texte, comme `recipeIngredients`.
     * Propre à la LISTE : à l'achat elle ne passe pas dans l'inventaire, qui
     * reste sans quantités (voir `inventoryItems`). Savoir qu'il faut 400 g
     * de farine aide à l'acheter ; savoir qu'il en reste 130 g à la maison
     * demanderait de tenir un compteur que personne ne tient.
     */
    quantityText: text('quantity_text'),
    /** Non nul = archivé dans une sortie validée. */
    tripId: text('trip_id'),
  },
  (t) => [
    index('shopping_pull').on(t.householdId, t.rev),
    index('shopping_tri').on(t.householdId, t.state, t.rayonSortOrder),
    check('shopping_state', sql`${t.state} in ('a_prendre','pris')`),
    // Soit un produit du catalogue, soit du texte libre — jamais les deux,
    // jamais aucun des deux.
    check(
      'shopping_source',
      sql`(${t.catalogItemId} is not null) <> (${t.freeText} is not null)`,
    ),
  ],
)

/** Sortie de courses validée. Un enregistrement par passage en caisse. */
export const trips = sqliteTable(
  'trips',
  {
    ...colonnesSync,
    validatedAt: integer('validated_at', { mode: 'timestamp' }),
    validatedBy: text('validated_by'),
    storeName: text('store_name'),
    itemCount: integer('item_count').notNull().default(0),
  },
  (t) => [index('trips_pull').on(t.householdId, t.rev)],
)

export const recipes = sqliteTable(
  'recipes',
  {
    ...colonnesSync,
    title: text('title').notNull(),
    servings: integer('servings'),
    prepMinutes: integer('prep_minutes'),
    cookMinutes: integer('cook_minutes'),
    steps: text('steps', { mode: 'json' }).$type<string[]>().notNull().default(sql`'[]'`),
    /** Stocké pour référence, JAMAIS récupéré par le serveur : pas de SSRF. */
    sourceUrl: text('source_url'),
    notes: text('notes'),
    tags: text('tags', { mode: 'json' }).$type<string[]>().notNull().default(sql`'[]'`),
    createdBy: text('created_by'),
  },
  (t) => [index('recipes_pull').on(t.householdId, t.rev)],
)

/**
 * `optional` est la colonne qui rend le verdict « ai-je tout ? » utilisable :
 * sans elle, une branche de persil manquante ferait échouer un bœuf
 * bourguignon complet.
 */
export const recipeIngredients = sqliteTable(
  'recipe_ingredients',
  {
    ...colonnesSync,
    recipeId: text('recipe_id').notNull(),
    catalogItemId: text('catalog_item_id'),
    freeText: text('free_text'),
    quantityText: text('quantity_text'),
    optional: integer('optional', { mode: 'boolean' }).notNull().default(false),
    sortOrder: integer('sort_order').notNull().default(0),
  },
  (t) => [
    index('ringredients_pull').on(t.householdId, t.rev),
    index('ringredients_recipe').on(t.recipeId),
  ],
)

/**
 * Planning. `date` en texte YYYY-MM-DD local : une semaine de menus n'est
 * pas un instant, et la stocker en epoch UTC la décalerait une nuit sur deux.
 */
export const mealPlanEntries = sqliteTable(
  'meal_plan_entries',
  {
    ...colonnesSync,
    date: text('date').notNull(),
    /** 'midi' | 'soir' */
    slot: text('slot').notNull(),
    recipeId: text('recipe_id'),
    freeText: text('free_text'),
    servings: integer('servings'),
    /** Prénoms des convives — sert à croiser avec les goûts. */
    diners: text('diners', { mode: 'json' }).$type<string[]>().notNull().default(sql`'[]'`),
  },
  (t) => [
    index('plan_pull').on(t.householdId, t.rev),
    uniqueIndex('plan_slot').on(t.householdId, t.date, t.slot),
    check('plan_slot_valide', sql`${t.slot} in ('midi','soir')`),
  ],
)

/** Personne du foyer — y compris celles qui n'ont pas de compte (enfants). */
export const people = sqliteTable(
  'people',
  {
    ...colonnesSync,
    name: text('name').notNull(),
    userId: text('user_id'),
    emoji: text('emoji'),
    color: text('color'),
  },
  (t) => [index('people_pull').on(t.householdId, t.rev)],
)

/**
 * Préférences alimentaires. `personId` nul = règle de foyer (« on ne mange
 * pas de porc »).
 *
 * Double écriture volontaire : les champs structurés servent aux badges de
 * l'app, mais `freeText` est la phrase humaine qui fait autorité — c'est
 * elle que l'assistant lit le mieux (« Léa déteste la texture des
 * champignons cuits mais mange les crus »), et aucune énumération ne
 * capturerait ça.
 */
export const preferences = sqliteTable(
  'preferences',
  {
    ...colonnesSync,
    personId: text('person_id'),
    /** 'allergie' | 'regime' | 'n_aime_pas' | 'regle' */
    kind: text('kind').notNull(),
    /** 'interdit' | 'evite' | 'prefere' */
    severity: text('severity').notNull().default('evite'),
    targetCatalogItemId: text('target_catalog_item_id'),
    freeText: text('free_text'),
    note: text('note'),
  },
  (t) => [
    index('preferences_pull').on(t.householdId, t.rev),
    check('pref_kind', sql`${t.kind} in ('allergie','regime','n_aime_pas','regle')`),
    check('pref_severity', sql`${t.severity} in ('interdit','evite','prefere')`),
  ],
)

/* ===================================================================== */
/* Relations (confort de requête, aucune contrainte supplémentaire)      */
/* ===================================================================== */

export const catalogRelations = relations(catalogItems, ({ one, many }) => ({
  rayon: one(rayons, { fields: [catalogItems.rayonId], references: [rayons.id] }),
  defaultLocation: one(locations, {
    fields: [catalogItems.defaultLocationId],
    references: [locations.id],
  }),
  inventaire: many(inventoryItems),
}))

export const inventoryRelations = relations(inventoryItems, ({ one, many }) => ({
  produit: one(catalogItems, {
    fields: [inventoryItems.catalogItemId],
    references: [catalogItems.id],
  }),
  lieu: one(locations, { fields: [inventoryItems.locationId], references: [locations.id] }),
  peremptions: many(expiries),
}))

export const recipeRelations = relations(recipes, ({ many }) => ({
  ingredients: many(recipeIngredients),
}))

/** Toutes les tables qui descendent sur les téléphones, dans l'ordre du pull. */
export const TABLES_SYNCHRONISEES = [
  'locations',
  'rayons',
  'catalogItems',
  'inventoryItems',
  'expiries',
  'shoppingItems',
  'trips',
  'recipes',
  'recipeIngredients',
  'mealPlanEntries',
  'people',
  'preferences',
] as const

export type TableSynchronisee = (typeof TABLES_SYNCHRONISEES)[number]

/* ===================================================================== */
/* Cache des Client ID Metadata Documents (CIMD)                         */
/* ===================================================================== */
/**
 * Claude s'identifie auprès de notre serveur d'autorisation par une URL —
 * son Client ID Metadata Document — que NOUS devons aller chercher côté
 * serveur au moment du consentement.
 *
 * Le problème, vérifié et documenté (anthropics/claude-code#84263) :
 * claude.ai est derrière Cloudflare, qui répond 403 à des requêtes serveur
 * venant d'IP cloud à mauvaise réputation. C'est fondé sur la réputation
 * d'IP et non sur le User-Agent — on ne peut donc pas le contourner.
 *
 * D'où cette table. Les deux documents connus sont SEMÉS à la migration, on
 * tente un rafraîchissement avec un timeout court, et on sert PÉRIMÉ en cas
 * d'échec. Un écran de consentement ne doit jamais dépendre d'un appel
 * réseau sortant pour s'afficher.
 */
export const oauthCimdCache = sqliteTable('oauth_cimd_cache', {
  /** L'URL du document — c'est elle qui EST le client_id. */
  clientId: text('client_id').primaryKey(),
  document: text('document', { mode: 'json' }).notNull(),
  fetchedAt: integer('fetched_at', { mode: 'timestamp' })
    .notNull()
    .default(sql`(unixepoch())`),
  /** Dernier échec, pour diagnostiquer sans fouiller les logs du conteneur. */
  lastErrorAt: integer('last_error_at', { mode: 'timestamp' }),
  lastError: text('last_error'),
})

/* ===================================================================== */
/* Journal des appels MCP                                                */
/* ===================================================================== */
/**
 * C'est ce qui rend supportable l'invariant « aucun outil MCP ne peut
 * supprimer une ligne » : c'est l'information d'annulation du foyer, et le
 * seul moyen de répondre à « pourquoi y a-t-il soudain 14 kg de farine ».
 */
export const mcpAudit = sqliteTable(
  'mcp_audit',
  {
    id: text('id').primaryKey(),
    householdId: text('household_id').notNull(),
    userId: text('user_id').notNull(),
    clientId: text('client_id'),
    tool: text('tool').notNull(),
    argsDigest: text('args_digest'),
    /** Résumé d'une ligne, en français, lisible dans les réglages. */
    summary: text('summary').notNull(),
    createdAt: integer('created_at', { mode: 'timestamp' })
      .notNull()
      .default(sql`(unixepoch())`),
  },
  (t) => [index('mcp_audit_household').on(t.householdId, t.createdAt)],
)
